import {
  BillingNumberRules,
  BillingRule,
  BillingRuleType,
} from '../../../interfaces/billing-rules.interfaces';
import {
  BillingNumberCharacterInfo,
  BillingNumberTooltipInfo,
  CharacterInfo,
  Pluralize,
} from './types';

export class BillingRulePluralize implements Pluralize {
  constructor(private readonly rule: BillingRule) {}

  public range(): string {
    if (this.rule.range.length === 1) {
      return `${this.rule.range.start}`;
    }

    return `${this.rule.range.start} to ${this.rule.range.end}`;
  }

  public required(): string {
    return `${this.rule.required ? '' : 'NOT '}Required`;
  }
}

export class FixedCharacters implements CharacterInfo {
  constructor(
    private readonly plurize: Pluralize,
    private readonly definition: string
  ) {}

  public info(): BillingNumberCharacterInfo {
    return {
      position: this.plurize.range(),
      requirement: this.plurize.required(),
      type: `Fixed as "${this.definition}"`,
    };
  }

  public disclaimer(): string {
    return 'These character position(s) are fixed and can not be altered:';
  }
}

export class InputCharacters implements CharacterInfo {
  private readonly types: Record<BillingRuleType, string>;

  constructor(
    private readonly plurize: Pluralize,
    private readonly chars: BillingRuleType[]
  ) {
    this.types = {
      alpha: 'Alpha',
      numeric: 'Numeric',
      special: 'Special Characters',
    };
  }

  public info(): BillingNumberCharacterInfo {
    const types = this.chars.map((char) => this.types[char]).join(' & ');
    return {
      position: this.plurize.range(),
      requirement: this.plurize.required(),
      type: `Valid Character Types: ${types}`,
    };
  }

  public disclaimer(): string {
    return 'These character position(s) can be customized:';
  }
}

export class BillingNumberTooltip {
  get mandatory(): boolean {
    return this.billing.mandatory;
  }

  constructor(private readonly billing: BillingNumberRules) {}

  public example(): string {
    return this.billing.example;
  }

  public structure(): BillingNumberTooltipInfo[] {
    return this.billing.rules
      .map((rule) => this.characters(rule))
      .map((characters) => ({
        entry: characters.disclaimer(),
        characters: characters.info(),
      }));
  }

  private characters(rule: BillingRule): CharacterInfo {
    const pluralize = new BillingRulePluralize(rule);

    if (rule.entry === 'fixed') {
      return new FixedCharacters(pluralize, rule.definition);
    }

    return new InputCharacters(pluralize, rule.char);
  }
}
